import { stegaClean } from "next-sanity";

import cn from "classnames";

import { generateUrl, Link } from "@/components/link/link.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import type { Theme } from "@/sanity.types";

import type {
  SmbHorizontalCardsProps,
  SmbHorizontalCardsVariant,
} from "./smbHorizontalCards.types";
import { SmbHorizontalCardsCard } from "./smbHorizontalCardsCard.component";
import { SmbHorizontalCardsCarousel } from "./smbHorizontalCardsCarousel.component";

// Full class names are spelled out so Tailwind can detect them at build time.
const THEME_BACKGROUND: Record<Theme, string> = {
  primary: "bg-primary",
  secondary: "bg-secondary",
  secondary100: "bg-secondary-100",
  tertiary: "bg-primary-100",
  light: "bg-greyscale-background",
  white: "bg-white",
  success: "bg-success",
  error: "bg-error",
  recommended: "bg-primary",
  promo: "bg-success",
};

const VARIANTS: SmbHorizontalCardsVariant[] = [
  "imageCarousel",
  "productTiles",
  "textCarousel",
];

export function SmbHorizontalCards({ block }: SmbHorizontalCardsProps) {
  const cleanVariant = stegaClean(block.variant);
  const variant = VARIANTS.includes(cleanVariant)
    ? cleanVariant
    : "imageCarousel";
  const theme = stegaClean(block.sectionTheme);
  const cards = (block.cards ?? []).filter((card) => card.title?.trim());

  if (!block.heading?.trim() || !cards.length) return null;

  const headingId = `smb-horizontal-cards-${block._key}`;
  const renderCards = () =>
    cards.map((card) => (
      <SmbHorizontalCardsCard key={card._key} card={card} variant={variant} />
    ));

  const disclaimer = block.disclaimer?.length ? (
    <div
      data-testid="smb-horizontal-cards-disclaimer"
      className="text-greyscale-body w-full text-center">
      <RichText
        blockComponents={{
          normal: ({ children }) => (
            <Text variant="legal" component="p">
              {children}
            </Text>
          ),
        }}>
        {block.disclaimer}
      </RichText>
    </div>
  ) : null;

  const renderLink = () => {
    const label = block.ctaLabel?.trim();
    const href = generateUrl({ link: block.ctaLink });
    if (!label || !href) return null;
    const openInNewTab = block.ctaLink?.openInNewTab;
    return (
      <Link
        href={href}
        target={openInNewTab ? "_blank" : undefined}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        className="text-secondary-100 text-center text-sm underline underline-offset-2">
        {label}
      </Link>
    );
  };

  return (
    <section
      data-testid="smb-horizontal-cards"
      data-variant={variant}
      aria-labelledby={headingId}
      className={cn("w-full", THEME_BACKGROUND[theme] ?? "bg-white")}>
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-8 px-6 py-10 lg:gap-12 lg:px-12">
        <header className="flex flex-col items-center gap-4 text-center">
          <Text
            id={headingId}
            variant="h3"
            component="h2"
            className="text-greyscale-headline">
            {block.heading}
          </Text>
          {block.subtitle?.trim() ? (
            <Text variant="body1" component="p" className="text-greyscale-body">
              {block.subtitle}
            </Text>
          ) : null}
        </header>

        {variant === "productTiles" ? (
          <>
            <SmbHorizontalCardsCarousel
              variant={variant}
              ariaLabel={`${block.heading} pages`}
              footer={disclaimer}
              className="lg:hidden">
              {renderCards()}
            </SmbHorizontalCardsCarousel>
            <div
              data-testid="smb-horizontal-cards-grid"
              className="hidden w-full gap-6 lg:grid lg:grid-cols-4">
              {renderCards()}
            </div>
            {disclaimer ? (
              <div className="hidden w-full lg:block">{disclaimer}</div>
            ) : null}
          </>
        ) : (
          <SmbHorizontalCardsCarousel
            variant={variant}
            ariaLabel={`${block.heading} pages`}
            footer={disclaimer}>
            {renderCards()}
          </SmbHorizontalCardsCarousel>
        )}

        {renderLink()}
      </div>
    </section>
  );
}
